import React from 'react'
import { useCallback, useRef, useState } from 'react'
import { useActiveDomainChangeEffect } from '../../../hooks/use-active-domain-change-effect'
import { useHistory, useParams } from 'react-router-dom'
import { parseQueryStringParams } from '../../../_utils/query-string'
import { AppState } from '../../../stores/app'
import { useService } from '@pushly/aqe/lib/hooks'
import { AppService } from '../../../services'
import { titleCase } from '@pushly/models/lib/utils/change-case'
import { AppMessageBuilderContext } from '../context'
import { useAppMessageReducer } from '../../../components/app-message-builder/reducer'
import { getClassNames } from '../../../_utils/classnames'
import { simpleNotification } from '@pushly/aqe/lib/utils'
import { AppMessageTemplateService } from '../../../services/app-message-template.service'
import { onResponseError403 } from '../../../_utils/on-response-error-403'
import { AppMessageUiTemplate } from '@pushly/models/lib/structs/app-messages/app-message-ui-template'
import { getAMPreviewConfig, cleanAndValidateMessage, cleanAndValidateTemplate } from './helpers'
import { getEnabledDeliveryChannels } from '../../../_utils/domain'
import { DeliveryChannel } from '@pushly/aqe/lib/enums/delivery-channels'
import { AppMessagePreview } from '../../../components/app-message-builder/elements/app-message-preview'
import { tryParseInt } from '../../../_utils/try-parse'
import { ControlDensityProvider, PageHeader } from '@pushly/aqe/lib/components'
import { AppMessageBuilderAside } from '../../../components/app-message-builder/elements/app-message-builder-aside'
import { AppMessageBuilderMain } from '../../../components/app-message-builder/elements/app-message-builder-main'
import { AppMessagesLoader } from '../../../components/app-message-builder/app-messages-loader'
import { AppMessageBasicsBuilder } from '../../../components/app-message-builder/elements/app-message-basics-builder'
import { AppMessageContentBuilder } from '../../../components/app-message-builder/elements/app-message-content-builder'
import { AppMessageBehaviorSummary } from '../../../components/app-message-builder/elements/app-message-behavior-summary'
import { AppMessageAudienceBuilder } from '../../../components/app-message-builder/elements/app-message-audience-builder'
import { AppMessageReadinessPanel } from '../../../components/app-message-builder/elements/app-message-readiness-panel'
import { AppMessageBuilderFooter } from '../../../components/app-message-builder/elements/app-message-builder-footer'
import { AppMessageService } from '../../../services/app-message.service'
import { AppMessageBuilderPayload } from '../../../components/app-message-builder/model-extensions'
import { AppMessageBuilderReach } from '../../../components/app-message-builder/elements/app-message-builder-reach'
import { getAppMessageReadiness } from '../../../components/app-message-builder/readiness'
import { InvalidFieldException } from '@pushly/models/lib/exceptions/invalid-field-exception'
import { MissingFieldException } from '@pushly/models/lib/exceptions/missing-field-exception'
import { extractFriendlyErrorMessage } from '../../../exceptions/helpers'
import { AppMessageBuilderFieldNamesMap } from './constants'
import { EditorActionRef } from '../../../components/custom-quill-editor/types'
import { Loading3QuartersOutlined } from '@ant-design/icons'
import { Alert, Flex, Skeleton } from 'antd'

import '../../../components/app-message-builder/styles/app-message-builder.scss'

export const BuildAppMessage = () => {
    const appState = useService(AppState)
    const appSvc = useService(AppService)
    const appMsgSvc = useService(AppMessageService)
    const appMsgTemplateSvc = useService(AppMessageTemplateService)
    const domain = appState.currentDomain!

    // Component uses url pathname to determine what view and mode we are in
    const pathCrumbs = location.pathname.split('/app-messages/')[1]
    const type = pathCrumbs.includes('templates') ? 'template' : 'message'
    let mode: 'create' | 'edit' = pathCrumbs.includes('new') ? 'create' : 'edit'
    const viewRoute = `/app-messages${type === 'template' ? '/templates' : ''}`

    const { push: updateRoute } = useHistory()
    const qs = parseQueryStringParams()

    let requestedAppMsgId
    let requestedTemplateId

    // create should attempt to parse ids to load if duplicate
    if (mode === 'create') {
        requestedAppMsgId = tryParseInt(qs.get('app_msg_id') ?? '') || undefined
        requestedTemplateId = tryParseInt(qs.get('template_id') ?? '') || undefined
    } else {
        const { appMessageId } = useParams<{ appMessageId: string }>()
        requestedAppMsgId = tryParseInt(appMessageId) || undefined

        const { appMessageTemplateId } = useParams<{ appMessageTemplateId: string }>()
        requestedTemplateId = tryParseInt(appMessageTemplateId) || undefined
    }

    const bodyRef = useRef<EditorActionRef>(null)
    const [submitting, setSubmitting] = useState(false)

    const [state, dispatch] = useAppMessageReducer({ domain, mode, type, defaultSelectedId: requestedTemplateId })
    const selectedVariant = state.variants[state.selectedVariantIdx]
    const channels = selectedVariant.template!.channels

    useActiveDomainChangeEffect((activeDomain, appService) => {
        appService.routeWithinDomain(viewRoute)
    })

    /**
     * Leaves the builder for the list it was opened from.
     *
     * Cancel used to call `routeBack()`, which reads a previous route the app
     * only records when it has routed itself. Opening the form from a pasted URL
     * or a fresh tab leaves it unset, and the history fallback then has nothing
     * to go back to — so Cancel did nothing at all, which is how most people
     * reach a shared link. A named destination cannot fail that way, and it is
     * where a successful save already lands.
     */
    const goBackToAppMessages = () => {
        updateRoute(`/domains/${domain.id}${viewRoute}`)
    }

    const enabledChannels: DeliveryChannel[] = getEnabledDeliveryChannels(domain).filter(
        (ch) => ch !== DeliveryChannel.WEB,
    )
    let channelTargetDisabled: DeliveryChannel | undefined
    if (
        selectedVariant.template!.channels.length === 1 &&
        !enabledChannels.includes(selectedVariant.template!.channels[0])
    ) {
        channelTargetDisabled = selectedVariant.template!.channels[0]
    }

    /**
     * An internal user may not send on a domain that reaches a real audience.
     *
     * Every exception is a domain where nothing does: a demo domain or demo
     * template, an internal domain, or a test domain — one carrying the
     * production domain it shadows. Blocking those blocks internal users from
     * exercising the feature on exactly the domains that exist for it.
     */
    const submitAllowed =
        !domain?.isPreviewDomain &&
        (!appState.currentUser?.isInternalUser ||
            domain?.isDemoDomain ||
            domain?.isDemo ||
            domain?.isDemoTemplate ||
            domain?.isInternal ||
            domain?.isTestDomain)

    const submitText = state.type === 'template' ? (state.mode === 'create' ? 'Create' : 'Update Template') : 'Schedule'

    const builderState = { ...state, disabled: !!channelTargetDisabled }
    const readiness = getAppMessageReadiness(builderState)

    const handleSubmit = async () => {
        let saved: boolean = false
        setSubmitting(true)

        try {
            if (state.type === 'template') {
                let valid: AppMessageUiTemplate | undefined
                valid = await cleanAndValidateTemplate(state, selectedVariant.template!, bodyRef)

                if (valid) {
                    let response
                    if (mode === 'create') {
                        response = await appMsgTemplateSvc.create(domain.id, valid, {
                            cancellationKey: `am-template-post`,
                        })
                    } else {
                        response = await appMsgTemplateSvc.put(domain.id, requestedTemplateId, valid, {
                            cancellationKey: `am-template-put${requestedTemplateId}`,
                        })
                    }

                    saved = response.ok
                }
            } else {
                let valid: AppMessageBuilderPayload = await cleanAndValidateMessage(state, selectedVariant, bodyRef)

                if (valid) {
                    let response
                    if (mode === 'create') {
                        response = await appMsgSvc.create(domain.id, valid, {
                            cancellationKey: `am-post`,
                        })
                    } else {
                        response = await appMsgSvc.patch(domain.id, requestedAppMsgId, valid, {
                            cancellationKey: `am-patch`,
                        })
                    }

                    saved = response.ok
                }
            }
        } catch (error) {
            if (error instanceof InvalidFieldException || error instanceof MissingFieldException) {
                error = extractFriendlyErrorMessage(error, {
                    rootModel: error.context.constructor,
                    fieldNamesMap: AppMessageBuilderFieldNamesMap,
                    startWithProperty: true,
                })
            }

            simpleNotification('error', error.message.replace('[object Object]', ''))
        }

        setSubmitting(false)

        if (saved) {
            goBackToAppMessages()
        }
    }

    const previewConfig: AppMessageUiTemplate = getAMPreviewConfig(selectedVariant.template!)

    const handleSendIntegrationLinkClick = useCallback(() => {
        const historyState = {
            containerId: 'send-integrations',
            returnTo: window.location.pathname,
        }

        appSvc.routeWithinDomain('#settings', false, historyState)
    }, [])

    return (
        <>
            {/* No action here: the form's own footer carries Cancel beside
                Schedule, where the decision is actually made. A second one in
                the page header was a duplicate of it. */}
            <PageHeader
                title={`${titleCase(mode)} App Message${type === 'template' ? ' Template' : ''}`}
                onTitleSet={appSvc.customizeTabTitle}
            />
            <AppMessageBuilderContext.Provider value={{ state: builderState, dispatch }}>
                <ControlDensityProvider density="comfortable" className={getClassNames('app-message-builder-density')}>
                    <AppMessagesLoader
                        domainId={domain.id}
                        appMsgId={requestedAppMsgId}
                        channels={channels}
                        errorHandler={onResponseError403(() => {
                            goBackToAppMessages()
                        })}
                    />
                    <Flex
                        justify="flex-start"
                        gap={24}
                        align="flex-start"
                        style={{ flexDirection: 'row-reverse', width: '100%' }}
                        className={getClassNames('app-message-builder')}
                    >
                        <AppMessageBuilderAside>
                            {state.type === 'message' && <AppMessageBuilderReach domain={domain} />}
                            <AppMessagePreview
                                template={previewConfig}
                                loading={state.loading ? <Loading3QuartersOutlined spin={true} /> : false}
                            />
                            <AppMessageReadinessPanel
                                readiness={readiness}
                                actionName={state.type === 'template' ? 'Save' : 'Schedule'}
                                loading={state.loading}
                            />
                        </AppMessageBuilderAside>

                        {state.loading ? (
                            <Skeleton active={true} loading={true} />
                        ) : (
                            <AppMessageBuilderMain>
                                {channelTargetDisabled && (
                                    <Alert
                                        className="app-message-builder-alert"
                                        type="warning"
                                        showIcon={true}
                                        message=""
                                        description={
                                            <span>
                                                This App Message is configured to target the Native&nbsp;
                                                {DeliveryChannel.getShortName(channelTargetDisabled)} Send Integration
                                                which is currently not enabled. Please enable the&nbsp;
                                                <a onClick={handleSendIntegrationLinkClick}>Send Integration</a> before
                                                attempting to edit.
                                            </span>
                                        }
                                    />
                                )}

                                <AppMessageBasicsBuilder
                                    domain={domain}
                                    channelTargetDisabled={channelTargetDisabled}
                                />

                                <AppMessageContentBuilder domain={domain} ref={bodyRef} />

                                {state.type === 'message' && (
                                    <>
                                        <AppMessageAudienceBuilder domain={domain} />
                                        <AppMessageBehaviorSummary readiness={readiness} />
                                    </>
                                )}

                                <AppMessageBuilderFooter
                                    readiness={readiness}
                                    submitText={submitText}
                                    submitAllowed={submitAllowed}
                                    submitting={submitting}
                                    onSubmit={handleSubmit}
                                    onCancel={goBackToAppMessages}
                                />
                            </AppMessageBuilderMain>
                        )}
                    </Flex>
                </ControlDensityProvider>
            </AppMessageBuilderContext.Provider>
        </>
    )
}
